import { useEffect, useState } from 'react';

import type { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  Link,
  useNavigate,
  useRouter,
  useSearch,
} from '@tanstack/react-router';

import { Box, Button } from "@src/components";
import { useAuthentication } from '@src/context/hooks';
import {
  EmailField,
  Form,
  PasswordField,
  Submit,
  TextField,
} from '@src/corelib/forms/generic';
import { createSchema, Yup } from '@src/corelib/validation';

import AuthLayout from './AuthLayout';

type LoginValues = {
  email: string;
  password: string;
};

type TwoFactorValues = {
  otp_code: string;
};

type LoginSearch = {
  redirect?: string;
  loginAs?: string;
};

type TwoFactorChallenge = {
  two_factor_required: boolean;
  challenge_token: string;
};

type EnrollmentNoticeResponse = {
  two_factor_enrollment_email_sent: boolean;
  detail?: string;
};

const StyleSheet = {
  actions: {
    mt: '16px',
  },
  qrCode: {
    width: '220px',
    height: '220px',
    mt: '16px',
    mb: '8px',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
  },
  links: {
    display: 'flex',
    justifyContent: 'center',
    '& a': (theme: Theme) => ({
      color: theme.palette.common.black,
      textDecoration: 'none',
    }),
    marginTop: '16px',
  },
};

const loginSchema = createSchema({
  email: { type: 'email', label: 'Email' },
  password: { type: 'password', label: 'Password' },
});
const twoFactorSchema = createSchema({
  otp_code: Yup.string()
    .required('Verification code is required')
    .matches(/^\d{6}$/, 'Enter a valid 6-digit code'),
});
const LOGIN_DEFAULT_VALUES = { email: '', password: '' };
const TWO_FACTOR_DEFAULT_VALUES = { otp_code: '' };

function Login() {
  const auth = useAuthentication();
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [enrollmentNotice, setEnrollmentNotice] = useState('');

  const router = useRouter();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as LoginSearch;
  const redirect = search.redirect ?? '/dashboard';
  const loginAs = search.loginAs;
  const [isLoginAsPending, setIsLoginAsPending] = useState(!!loginAs);

  useEffect(() => {
    if (loginAs) {
      auth.refreshToken(loginAs).then(() => {
        router.invalidate().finally(() => {
          navigate({ to: decodeURIComponent(redirect) });
        });
      }).catch(() => {
        setIsLoginAsPending(false);
      });
    }
  }, [loginAs]);


  const completeLogin = () =>
    router.invalidate().finally(() => {
      navigate({ to: decodeURIComponent(redirect) as '/' });
    });

  const isTwoFactorChallenge = (
    response: unknown,
  ): response is TwoFactorChallenge => {
    if (!response || typeof response !== 'object') {
      return false;
    }
    const data = response as Record<string, unknown>;
    return (
      data.two_factor_required === true
      && typeof data.challenge_token === 'string'
    );
  };

  const isEnrollmentNotice = (
    response: unknown,
  ): response is EnrollmentNoticeResponse => {
    if (!response || typeof response !== 'object') {
      return false;
    }
    const data = response as Record<string, unknown>;
    return data.two_factor_enrollment_email_sent === true;
  };

  const login = (values: Record<string, unknown>) => {
    const payload: LoginValues = {
      email: String(values.email ?? ''),
      password: String(values.password ?? ''),
    };

    return new Promise<void>((resolve, reject) => {
      auth
        .login(payload.email, payload.password)
        .then((response) => {
          if (isTwoFactorChallenge(response)) {
            setEnrollmentNotice('');
            setChallenge(response);
            resolve();
            return;
          }
          if (isEnrollmentNotice(response)) {
            setChallenge(null);
            setEnrollmentNotice(response.detail ?? 'Check your email inbox.');
            resolve();
            return;
          }
          setEnrollmentNotice('');
          completeLogin().finally(resolve);
        })
        .catch(reject);
    });
  };

  const verifyTwoFactor = (values: Record<string, unknown>) => {
    const payload: TwoFactorValues = {
      otp_code: String(values.otp_code ?? ''),
    };

    return new Promise<void>((resolve, reject) => {
      if (!challenge?.challenge_token) {
        reject(new Error('Missing challenge token.'));
        return;
      }

      auth
        .verifyTwoFactor(challenge.challenge_token, payload.otp_code)
        .then(() => {
          completeLogin().finally(resolve);
        })
        .catch(reject);
    });
  };

  const resetChallenge = () => {
    setChallenge(null);
  };

  return (
    <AuthLayout>
      <Typography variant="h3" align="center" component="h1">
        {challenge ? 'Two-Factor Authentication' : 'Login'}
      </Typography>

      {enrollmentNotice ? (
        <>
          <Typography variant="body2" align="center" color="info.main">
            {enrollmentNotice}
          </Typography>
          <Box sx={StyleSheet.links}>
            <Button variant="text" onClick={ () => setEnrollmentNotice('') }>Back to login</Button>
          </Box>
        </>
      ) : (
        <>
          {!challenge && !isLoginAsPending && (
            <Form
              schema={loginSchema}
              sx={{ width: '100%' }}
              defaultValues={LOGIN_DEFAULT_VALUES}
              onSubmit={login}
            >
              <EmailField required fullWidth name="email" label="Email" />
              <PasswordField
                required
                fullWidth
                name="password"
                label="Password"
              />

              <Box sx={StyleSheet.actions}>
                <Submit
                  fullWidth
                  variant="contained"
                  color="primary"
                  loadingPosition="start"
                >
                  Login
                </Submit>

                <Box sx={StyleSheet.links}>
                  <Link to="/auth/password-reset">
                    Forgot password?
                  </Link>
                </Box>
              </Box>
            </Form>
          )}

          {challenge && !isLoginAsPending &&(
            <Form
              schema={twoFactorSchema}
              sx={{ width: '100%' }}
              defaultValues={TWO_FACTOR_DEFAULT_VALUES}
              onSubmit={verifyTwoFactor}
            >
              <TextField
                required
                fullWidth
                name="otp_code"
                label="Verification code"
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />

              <Box sx={StyleSheet.actions}>
                <Submit
                  fullWidth
                  variant="contained"
                  color="primary"
                  loadingPosition="start"
                >
                  Verify code
                </Submit>
                <Button fullWidth variant="text" onClick={resetChallenge}>
                  Use another account
                </Button>
              </Box>
            </Form>
          )}
        </>
      )}
    </AuthLayout>
  );
}

Login.displayName = 'Login';
export default Login;
